import React, { useState } from 'react';
import { SearchResult } from '../types/research';

interface SearchResultsProps {
  searchResults: SearchResult[];
  isVisible: boolean;
  searchesCompleted: number;
  searchesTotal: number;
}

const SearchResults: React.FC<SearchResultsProps> = ({ 
  searchResults, 
  isVisible, 
  searchesCompleted,
  searchesTotal 
}) => {
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedResults);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedResults(newExpanded);
  };

  if (!isVisible || (searchResults.length === 0 && searchesCompleted === 0)) {
    return null;
  }

  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🔍</span>
              Search Results ({searchResults.length}/{searchesTotal})
            </h3>
            <div className="text-sm text-gray-600">
              {searchesCompleted} of {searchesTotal} completed
            </div>
          </div>
        </div>
        
        {searchResults.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {searchResults
              .sort((a, b) => a.search_index - b.search_index)
              .map((result) => {
                const isExpanded = expandedResults.has(result.search_index);
                const summaryPreview = result.result_summary.length > 200 
                  ? result.result_summary.substring(0, 200) + '...' 
                  : result.result_summary;
                
                return (
                  <div key={result.search_index} className="px-6 py-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-green-600 font-semibold text-sm">
                            ✓
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            🔍 Search {result.search_index + 1}
                          </span>
                          {result.timestamp && (
                            <span className="ml-2 text-xs text-gray-500">
                              {new Date(result.timestamp).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm font-medium text-gray-900 mb-2">
                          "{result.search_query}"
                        </p>
                        
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            💡 Reason
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          {result.search_reason}
                        </p>
                        
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            📄 Summary
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-700">
                          <p>{isExpanded ? result.result_summary : summaryPreview}</p>
                          
                          {result.result_summary.length > 200 && (
                            <button
                              onClick={() => toggleExpanded(result.search_index)}
                              className="mt-2 text-blue-600 hover:text-blue-800 text-xs font-medium"
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-gray-500">
            <div className="animate-pulse">
              <span className="text-2xl mb-2 block">🔍</span>
              <p>Searching the web...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults; 